import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Dialog, LibThumb, SearchIcon, Switch, useRunner } from "./parts";
import { roomsApi } from "./useRoom";
import AddToLibrary from "./AddToLibrary";
import { shortDate } from "../record/format.mjs";

/*
 * THE SMALLER WINDOWS: the library picker (LibraryPicker board), the four edit
 * dialogs (EditDialogs board: deck, pricing, recap is RecapDialog, agreement),
 * the PandaDoc link, and the email picker behind the panel's "+ From the
 * timeline" and "+ From a follow-up".
 */

/* ------------------------------------------------------------ picker -- */

export function LibraryPicker({ companyId, companyName, onBoard = [], onClose }) {
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const [picked, setPicked] = useState({});
  const [notes, setNotes] = useState({});
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const run = useRunner(setBusy, setErr);

  const load = () => roomsApi.library().then(({ items: rows }) => setItems(rows)).catch((e) => setErr(e.message));
  useEffect(() => {
    load();
  }, []);

  const on = new Map(onBoard.map((i) => [i.library_id, i]));
  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (items || []).filter((i) => !needle || `${i.title} ${i.kind_label} ${i.detail || ""}`.toLowerCase().includes(needle));
  }, [items, q]);
  const ids = Object.keys(picked).filter((k) => picked[k]).map(Number);
  // In picker order, so the first ticked ends up on top of the room.
  const ordered = shown.filter((i) => ids.includes(i.id)).map((i) => i.id);

  const add = () =>
    run(async () => {
      await roomsApi.add(companyId, { section: "decks", library_ids: ordered, notes });
      onClose(true);
    });

  return (
    <Dialog
      title={`Add to ${companyName}’s room`}
      sub="From Decks & examples · newest first on the room"
      size="md"
      onClose={() => onClose(false)}
      foot={
        <>
          <button type="button" className="rm-link" style={{ marginRight: "auto" }} onClick={() => setAdding((a) => !a)}>
            {adding ? "Back to the library" : "Upload something new…"}
          </button>
          {err && <span className="rm-err">{err}</span>}
          <button type="button" className="rm-btn" onClick={() => onClose(false)}>Cancel</button>
          <button type="button" className="rm-btn rm-btn--primary" disabled={!ordered.length || busy} onClick={add}>
            {busy ? "Adding…" : `Add ${ordered.length || ""} to room`.replace("  ", " ")}
          </button>
        </>
      }
    >
      {adding ? (
        <AddToLibrary
          compact
          onAdded={(item) => {
            setAdding(false);
            setPicked((p) => ({ ...p, [item.id]: true }));
            load();
          }}
        />
      ) : (
        <>
          <label className="rm-search">
            <SearchIcon />
            <span className="rm-sr">Search the library</span>
            <input placeholder="Search the library" value={q} onChange={(e) => setQ(e.target.value)} />
          </label>
          <div className="rm-opts">
            {items === null && <p className="rm-sub">Loading…</p>}
            {items && items.length === 0 && (
              <p className="rm-sub">
                The library is empty. Upload a deck or paste a link with “Upload something new…”, or open <Link to="/sales/library">Decks &amp; examples</Link>.
              </p>
            )}
            {shown.map((i) => {
              const already = on.get(i.id);
              const checked = !!picked[i.id];
              return (
                <div key={i.id} className={`rm-opt${checked ? " is-on" : ""}${already ? " is-off" : ""}`}>
                  <input
                    type="checkbox"
                    id={`rm-p-${i.id}`}
                    checked={!!already || checked}
                    disabled={!!already}
                    onChange={(e) => setPicked((p) => ({ ...p, [i.id]: e.target.checked }))}
                  />
                  <LibThumb item={i} />
                  <div className="rm-opt__b">
                    <label htmlFor={`rm-p-${i.id}`}>
                      <span style={{ fontWeight: 600 }}>{i.title}</span>
                      <br />
                      <span className="rm-sub">{[i.kind_label, i.detail].filter(Boolean).join(" · ")}</span>
                    </label>
                    {checked && !already && (
                      <div className="rm-field">
                        <label htmlFor={`rm-n-${i.id}`} style={{ fontSize: 12 }}>
                          Note for {companyName} <small>· optional, one line</small>
                        </label>
                        <input
                          id={`rm-n-${i.id}`}
                          className="rm-input"
                          placeholder="Shown under the title, in quotes"
                          value={notes[i.id] || ""}
                          maxLength={300}
                          onChange={(e) => setNotes((n) => ({ ...n, [i.id]: e.target.value }))}
                        />
                      </div>
                    )}
                  </div>
                  {already && <span className="rm-pill rm-pill--quiet" style={{ alignSelf: "center" }}>On the room · {shortDate(already.created_at)}</span>}
                </div>
              );
            })}
          </div>
        </>
      )}
    </Dialog>
  );
}

/* ------------------------------------------------------- edit: deck -- */

export function EditDeckDialog({ companyId, companyName, item, onClose }) {
  const [note, setNote] = useState(item.note || "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const run = useRunner(setBusy, setErr);
  const lib = item.library || {};
  const save = () =>
    run(async () => {
      await roomsApi.editItem(companyId, item.id, { note });
      onClose(true);
    });
  return (
    <Dialog
      eyebrow="Deck or example"
      title={`Edit on ${companyName}’s room`}
      size="sm"
      onClose={() => onClose(false)}
      foot={
        <>
          {err && <span className="rm-err" style={{ marginRight: "auto" }}>{err}</span>}
          <button type="button" className="rm-btn" style={{ marginLeft: "auto" }} onClick={() => onClose(false)}>Cancel</button>
          <button type="button" className="rm-btn rm-btn--primary" disabled={busy} onClick={save}>{busy ? "Saving…" : "Save"}</button>
        </>
      }
    >
      <div className="rm-strip" style={{ gap: 12 }}>
        <LibThumb item={lib} />
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 600 }}>{lib.title}</div>
          <div className="rm-meta">{[lib.kind_label, lib.detail].filter(Boolean).join(" · ")}</div>
        </div>
      </div>
      <div className="rm-hint">
        🔒 Change the title in the library, it changes everywhere. <Link to="/sales/library" style={{ fontWeight: 600 }} onClick={() => onClose(false)}>Open in library</Link>
      </div>
      <div className="rm-field">
        <label htmlFor="rm-ed-note">
          Note for {companyName} <small>· one line, in quotes under the title</small>
        </label>
        <input id="rm-ed-note" className="rm-input" value={note} maxLength={300} onChange={(e) => setNote(e.target.value)} />
      </div>
    </Dialog>
  );
}

/* ---------------------------------------------------- edit: pricing -- */

function quoteLine(q) {
  return (q && q.lines ? q.lines : []).join(" · ");
}

export function EditPricingDialog({ companyId, item, onClose }) {
  const p = item.pricing || {};
  const cur = p.current;
  const [options, setOptions] = useState(null);
  const [key, setKey] = useState(null);
  const [showPrev, setShowPrev] = useState(p.show_previous !== false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const run = useRunner(setBusy, setErr);

  useEffect(() => {
    roomsApi
      .pricingOptions(companyId)
      .then(({ options: o }) => {
        setOptions(o);
        const same = o.find((x) => x.lines.join("\n") === (cur ? cur.lines.join("\n") : ""));
        setKey(same ? same.key : null);
      })
      .catch((e) => setErr(e.message));
  }, [companyId, cur]);

  const save = () =>
    run(async () => {
      const body = { show_previous: showPrev };
      const same = options && options.find((x) => x.key === key);
      if (same && (!cur || same.lines.join("\n") !== cur.lines.join("\n"))) body.key = key;
      await roomsApi.editItem(companyId, item.id, body);
      onClose(true);
    });

  const v = (cur && cur.view) || {};
  return (
    <Dialog
      eyebrow="Pricing"
      title="Edit pricing on the room"
      size="sm"
      onClose={() => onClose(false)}
      foot={
        <>
          {err && <span className="rm-err" style={{ marginRight: "auto" }}>{err}</span>}
          <button type="button" className="rm-btn" style={{ marginLeft: "auto" }} onClick={() => onClose(false)}>Cancel</button>
          <button type="button" className="rm-btn rm-btn--primary" disabled={busy || !options} onClick={save}>{busy ? "Saving…" : "Save"}</button>
        </>
      }
    >
      <div className="rm-quote">
        <span className="rm-meta">On top</span>
        <span className="rm-quote__big">{v.base_label ? `${v.base_label}/mo base` : (cur && (v.shown || cur.lines)[0]) || "—"}</span>
        <span style={{ fontSize: 13 }}>{cur ? (v.shown || cur.lines || []).slice(1).join(" · ") : ""}{v.minimum ? ` · ${v.minimum}` : ""}</span>
        <span style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 2 }}>
          {p.previous && <span className="rm-pill rm-pill--dark">Revised</span>}
          <span className="rm-sub">{cur && cur.at ? `from your email of ${shortDate(cur.at)}` : ""}</span>
        </span>
      </div>
      {p.previous && (
        <div className="rm-block" style={{ borderBottom: "1px solid var(--rm-rule)", paddingBottom: 10, flexDirection: "row", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div style={{ fontWeight: 600, fontSize: 13.5 }}>Show the earlier quote it replaces</div>
            <div className="rm-meta">{p.previous.at ? `${shortDate(p.previous.at)} quote` : "The earlier quote"}, folded under it on the room</div>
          </div>
          <Switch on={showPrev} label="Show the earlier quote it replaces" onChange={setShowPrev} />
        </div>
      )}
      <fieldset style={{ border: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column" }}>
        <legend className="rm-field__label" style={{ paddingBottom: 6 }}>Use a different quote</legend>
        {options === null && <span className="rm-sub">Reading the quotes on record…</span>}
        {options && options.length === 0 && <span className="rm-sub">No pricing on this record.</span>}
        {(options || []).map((o) => (
          <label key={o.key} className={`rm-opt${key === o.key ? " is-on" : ""}`} style={{ cursor: "pointer" }}>
            <input type="radio" name="rm-quote" checked={key === o.key} onChange={() => setKey(o.key)} />
            <span className="rm-opt__b" style={{ gap: 0 }}>
              <span style={{ fontWeight: 600, fontSize: 13.5 }}>
                {o.at ? shortDate(o.at) : "—"} · {o.subject || (o.key === "approved" ? "Approved revision" : "Pricing email")}
              </span>
              <span className="rm-meta">
                {o.kind}
                {o.by === "them" ? " · their email" : ""} · {quoteLine(o)}
                {cur && o.lines.join("\n") === cur.lines.join("\n") ? " · on the room" : ""}
                {o.on_record ? " · What we quoted" : ""}
              </span>
            </span>
          </label>
        ))}
      </fieldset>
    </Dialog>
  );
}

/* -------------------------------------------------- edit: agreement -- */

export function EditAgreementDialog({ companyId, item, newer = false, onClose }) {
  const c = item.content || {};
  const isPd = c.kind === "pandadoc";
  const [label, setLabel] = useState(c.label || (isPd ? "Service agreement" : "Draft agreement (.docx)"));
  const [url, setUrl] = useState(c.url || "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const run = useRunner(setBusy, setErr);
  const save = () =>
    run(async () => {
      await roomsApi.editItem(companyId, item.id, isPd ? { label, url } : { label });
      onClose(true);
    });
  const replace = () =>
    run(async () => {
      await roomsApi.editItem(companyId, item.id, { replace: true });
      onClose(true);
    });
  return (
    <Dialog
      eyebrow="Agreement"
      title="Edit agreement on the room"
      size="sm"
      onClose={() => onClose(false)}
      foot={
        <>
          {err && <span className="rm-err" style={{ marginRight: "auto" }}>{err}</span>}
          <button type="button" className="rm-btn" style={{ marginLeft: "auto" }} onClick={() => onClose(false)}>Cancel</button>
          <button type="button" className="rm-btn rm-btn--primary" disabled={busy} onClick={save}>{busy ? "Saving…" : "Save"}</button>
        </>
      }
    >
      <div className="rm-field">
        <label htmlFor="rm-ea-label">Label on the room</label>
        <input id="rm-ea-label" className="rm-input" value={label} maxLength={80} onChange={(e) => setLabel(e.target.value)} />
      </div>
      {isPd ? (
        <div className="rm-field">
          <label htmlFor="rm-ea-pd">PandaDoc link</label>
          <input id="rm-ea-pd" className="rm-input" value={url} onChange={(e) => setUrl(e.target.value)} />
          <div className="rm-hint">Paste a new link to replace it.{c.sent_at ? ` Sent ${shortDate(c.sent_at)}.` : ""}</div>
        </div>
      ) : (
        <div className="rm-block">
          <div className="rm-field__label">Draft .docx</div>
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <span aria-hidden="true" className="rm-thumb" style={{ width: 28, height: 34, alignItems: "flex-end", paddingBottom: 3, fontSize: 7 }}>DOCX</span>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 600, fontSize: 13.5 }}>v{c.version || 1} · on the room</div>
              <div className="rm-meta">{[c.filename, c.generated_at ? shortDate(c.generated_at) : null].filter(Boolean).join(" · ")}</div>
            </div>
          </div>
          {newer ? (
            <div className="rm-newer">
              <span><b>The agreement terms changed since v{c.version || 1}.</b> A fresh draft reads them as they are now.</span>
              <button type="button" className="rm-btn rm-btn--primary rm-btn--sm" disabled={busy} onClick={replace}>Replace with v{(c.version || 1) + 1}</button>
            </div>
          ) : (
            <button type="button" className="rm-link" style={{ alignSelf: "flex-start" }} disabled={busy} onClick={replace}>
              Generate v{(c.version || 1) + 1} from the record now
            </button>
          )}
          <div className="rm-hint">The prospect then downloads the new version. Earlier versions stay on the record.</div>
        </div>
      )}
    </Dialog>
  );
}

/* ----------------------------------------------------- PandaDoc link -- */

export function PandaDocDialog({ companyId, onClose }) {
  const [url, setUrl] = useState("");
  const [label, setLabel] = useState("Service agreement");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const run = useRunner(setBusy, setErr);
  const add = () =>
    run(async () => {
      await roomsApi.add(companyId, { section: "agreement", kind: "pandadoc", url: url.trim(), label });
      onClose(true);
    });
  return (
    <Dialog
      eyebrow="Agreement"
      title="Add the PandaDoc link"
      size="sm"
      onClose={() => onClose(false)}
      foot={
        <>
          {err && <span className="rm-err" style={{ marginRight: "auto" }}>{err}</span>}
          <button type="button" className="rm-btn" style={{ marginLeft: "auto" }} onClick={() => onClose(false)}>Cancel</button>
          <button type="button" className="rm-btn rm-btn--primary" disabled={busy || !url.trim()} onClick={add}>{busy ? "Adding…" : "Add to room"}</button>
        </>
      }
    >
      <div className="rm-field">
        <label htmlFor="rm-pd-url">PandaDoc link</label>
        <input id="rm-pd-url" className="rm-input" placeholder="https://app.pandadoc.com/document/…" value={url} onChange={(e) => setUrl(e.target.value)} />
        <div className="rm-hint">Shows as “Review &amp; sign in PandaDoc”. A draft download stays beside it until you remove it.</div>
      </div>
      <div className="rm-field">
        <label htmlFor="rm-pd-label">Label on the room</label>
        <input id="rm-pd-label" className="rm-input" value={label} maxLength={80} onChange={(e) => setLabel(e.target.value)} />
      </div>
    </Dialog>
  );
}

/* ------------------------------------------------------- email picker -- */

/*
 * "+ From the timeline" and "+ From a follow-up": the emails already loaded on
 * the record, newest first. Follow-ups are our emails, and the ones after a
 * call come first.
 */
export function EmailPicker({ rows, followUps = false, onClose, onPick }) {
  const [q, setQ] = useState("");
  const emails = useMemo(() => {
    const list = (rows || [])
      .filter((r) => r.item_type === "activity" && r.subtype === "email" && String(r.body || "").trim())
      .filter((r) => !((r.metadata || {}).bulk))
      .filter((r) => !followUps || (r.direction || (r.metadata || {}).direction) === "outbound");
    const seen = new Set();
    return list.filter((r) => {
      const k = r.msg_id || r.id;
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    });
  }, [rows, followUps]);
  const needle = q.trim().toLowerCase();
  const shown = emails.filter((r) => !needle || `${r.title || ""} ${r.body || ""}`.toLowerCase().includes(needle)).slice(0, 60);
  return (
    <Dialog title={followUps ? "Which follow-up?" : "Which email?"} sub={followUps ? "Your emails on this record, newest first" : "Emails on this record, newest first"} size="md" onClose={() => onClose(false)}>
      <label className="rm-search">
        <SearchIcon />
        <span className="rm-sr">Search the emails</span>
        <input placeholder="Search subject and text" value={q} onChange={(e) => setQ(e.target.value)} />
      </label>
      <div className="rm-opts">
        {shown.length === 0 && <p className="rm-sub">No emails loaded on this record{needle ? " match" : ""}.</p>}
        {shown.map((r) => {
          const out = (r.direction || (r.metadata || {}).direction) === "outbound";
          return (
            <button key={r.id} type="button" className="rm-menu__item" style={{ borderBottom: "1px solid var(--rm-row)", borderRadius: 0 }} onClick={() => onPick(r)}>
              <b>
                {out ? "You" : r.sender ? [r.sender.first_name, r.sender.last_name].filter(Boolean).join(" ") || r.sender.email : "Them"}: {r.title || "(no subject)"}
              </b>
              <span>
                {shortDate(r.occurred_at)} · {String(r.body || "").replace(/\s+/g, " ").slice(0, 110)}
              </span>
            </button>
          );
        })}
      </div>
    </Dialog>
  );
}
